import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { api } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { Edit, Save, Trash2, Plus, Upload, X, Mail, Eye, LogOut, BarChart3, Users, TrendingUp, Calendar } from 'lucide-react';
import axios from 'axios';
import '../styles/AdminDashboard.css';

const AdminDashboard = () => {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const [activeTab, setActiveTab] = useState('content');
  const [content, setContent] = useState([]);
  const [services, setServices] = useState([]);
  const [projects, setProjects] = useState([]);
  const [messages, setMessages] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [editingItem, setEditingItem] = useState(null);

  useEffect(() => {
    loadData();
  }, [activeTab]);

  const handleLogout = () => {
    logout();
    toast.success('Erfolgreich abgemeldet');
    navigate('/login');
  };

  const loadData = async () => {
    setLoading(true);
    try {
      if (activeTab === 'content') {
        const response = await api.getAllContent();
        setContent(response.data);
      } else if (activeTab === 'services') {
        const response = await api.getServices();
        setServices(response.data);
      } else if (activeTab === 'projects') {
        const response = await api.getProjects();
        setProjects(response.data);
      } else if (activeTab === 'messages') {
        const response = await api.getContactMessages();
        setMessages(response.data);
      } else if (activeTab === 'analytics') {
        const response = await api.getAnalyticsStats();
        setStats(response.data);
      }
    } catch (error) {
      toast.error('Fehler beim Laden der Daten');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveContent = async (item) => {
    try {
      await api.updateContent(item.id, {
        section: item.section,
        key: item.key,
        value: item.value
      });
      toast.success('Inhalt gespeichert');
      setEditingItem(null);
      loadData();
    } catch (error) {
      toast.error('Fehler beim Speichern');
      console.error(error);
    }
  };

  const handleSaveService = async (service) => {
    try {
      if (service.id && !service.isNew) {
        await api.updateService(service.id, service);
        toast.success('Service aktualisiert');
      } else {
        await api.createService(service);
        toast.success('Service erstellt');
      }
      setEditingItem(null);
      loadData();
    } catch (error) {
      toast.error('Fehler beim Speichern');
      console.error(error);
    }
  };

  const handleDeleteService = async (id) => {
    if (!window.confirm('Möchten Sie diesen Service wirklich löschen?')) return;
    try {
      await api.deleteService(id);
      toast.success('Service gelöscht');
      loadData();
    } catch (error) {
      toast.error('Fehler beim Löschen');
      console.error(error);
    }
  };

  const handleSaveProject = async (project) => {
    try {
      if (project.id && !project.isNew) {
        await api.updateProject(project.id, project);
        toast.success('Projekt aktualisiert');
      } else {
        await api.createProject(project);
        toast.success('Projekt erstellt');
      }
      setEditingItem(null);
      loadData();
    } catch (error) {
      toast.error('Fehler beim Speichern');
      console.error(error);
    }
  };

  const handleDeleteProject = async (id) => {
    if (!window.confirm('Möchten Sie dieses Projekt wirklich löschen?')) return;
    try {
      await api.deleteProject(id);
      toast.success('Projekt gelöscht');
      loadData();
    } catch (error) {
      toast.error('Fehler beim Löschen');
      console.error(error);
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    try {
      toast.loading('Bild wird hochgeladen...');
      const response = await api.uploadImage(file);
      toast.dismiss();
      toast.success('Bild hochgeladen');
      return response.data.image_url;
    } catch (error) {
      toast.dismiss();
      toast.error('Fehler beim Hochladen');
      console.error(error);
      return null;
    }
  };

  const handleDeleteMessage = async (id) => {
    if (!window.confirm('Möchten Sie diese Nachricht wirklich löschen?')) return;
    try {
      await api.deleteContactMessage(id);
      toast.success('Nachricht gelöscht');
      loadData();
    } catch (error) {
      toast.error('Fehler beim Löschen');
      console.error(error);
    }
  };

  const handleMarkAsRead = async (id) => {
    try {
      await api.updateContactMessage(id, { status: 'read' });
      toast.success('Als gelesen markiert');
      loadData();
    } catch (error) {
      toast.error('Fehler beim Aktualisieren');
      console.error(error);
    }
  };

  const handleResetStats = async () => {
    if (!window.confirm('Möchten Sie wirklich ALLE Statistik-Daten unwiderruflich löschen?')) return;
    try {
      await api.resetAnalytics();
      toast.success('Statistik zurückgesetzt');
      loadData();
    } catch (error) {
      toast.error('Fehler beim Zurücksetzen');
      console.error(error);
    }
  };

  return (
    <div className="admin-dashboard">
      <div className="admin-header">
        <h1 className="admin-title">Admin Dashboard</h1>
        <div className="admin-header-actions">
          <a href="/" className="btn-secondary">Zur Website</a>
          <Button onClick={handleLogout} className="btn-secondary">
            <LogOut size={16} /> Abmelden
          </Button>
        </div>
      </div>

      <div className="admin-tabs">
        <button 
          className={`admin-tab ${activeTab === 'content' ? 'active' : ''}`}
          onClick={() => setActiveTab('content')}
        >
          Inhalte
        </button>
        <button 
          className={`admin-tab ${activeTab === 'services' ? 'active' : ''}`}
          onClick={() => setActiveTab('services')}
        >
          Services
        </button>
        <button 
          className={`admin-tab ${activeTab === 'projects' ? 'active' : ''}`}
          onClick={() => setActiveTab('projects')}
        >
          Projekte
        </button>
        <button 
          className={`admin-tab ${activeTab === 'messages' ? 'active' : ''}`}
          onClick={() => setActiveTab('messages')}
        >
          Nachrichten {messages.filter(m => m.status === 'new').length > 0 && `(${messages.filter(m => m.status === 'new').length})`}
        </button>
        <button 
          className={`admin-tab ${activeTab === 'analytics' ? 'active' : ''}`}
          onClick={() => setActiveTab('analytics')}
          data-testid="admin-tab-analytics"
        >
          Statistik
        </button>
      </div>

      <div className="admin-content">
        {loading ? (
          <div className="admin-loading">Lädt...</div>
        ) : (
          <>
            {activeTab === 'content' && (
              <ContentEditor 
                content={content}
                editingItem={editingItem}
                setEditingItem={setEditingItem}
                onSave={handleSaveContent}
              />
            )}
            {activeTab === 'services' && (
              <ServicesEditor 
                services={services}
                editingItem={editingItem}
                setEditingItem={setEditingItem}
                onSave={handleSaveService}
                onDelete={handleDeleteService}
              />
            )}
            {activeTab === 'projects' && (
              <ProjectsEditor 
                projects={projects}
                editingItem={editingItem}
                setEditingItem={setEditingItem}
                onSave={handleSaveProject}
                onDelete={handleDeleteProject}
                onImageUpload={handleImageUpload}
              />
            )}
            {activeTab === 'messages' && (
              <MessagesViewer 
                messages={messages}
                onDelete={handleDeleteMessage}
                onMarkAsRead={handleMarkAsRead}
              />
            )}
            {activeTab === 'analytics' && (
              <AnalyticsViewer 
                stats={stats}
                onRefresh={loadData}
                onReset={handleResetStats}
              />
            )}
          </>
        )}
      </div>
    </div>
  );
};

// Content Editor Component
const ContentEditor = ({ content, editingItem, setEditingItem, onSave }) => {
  const [uploading, setUploading] = useState(false);
  
  const groupedContent = content.reduce((acc, item) => {
    if (!acc[item.section]) acc[item.section] = [];
    acc[item.section].push(item);
    return acc;
  }, {});
  
  const handleImageUpload = async (e, item) => {
    const file = e.target.files[0];
    if (!file) return;
    
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const token = localStorage.getItem('admin_token');
      const response = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/upload`, formData, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      const imageUrl = response.data.image_url;
      setEditingItem({...item, value: imageUrl});
      toast.success('Bild hochgeladen!');
    } catch (error) {
      toast.error('Fehler beim Hochladen');
      console.error(error);
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="editor-container">
      {Object.entries(groupedContent).map(([section, items]) => (
        <Card key={section} className="editor-section">
          <h2 className="editor-section-title">{section.toUpperCase()}</h2>
          {items.map(item => (
            <div key={item.id} className="editor-item">
              <label className="editor-label">{item.key}</label>
              {editingItem?.id === item.id ? (
                <div className="editor-edit-mode">
                  {(item.key === 'team_photo' || item.key === 'additional_photo') ? (
                    <div className="image-upload-section">
                      <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => handleImageUpload(e, item)}
                        id={`upload-${item.id}`}
                        className="file-input"
                        disabled={uploading}
                      />
                      <label htmlFor={`upload-${item.id}`} className="upload-label">
                        <Upload size={20} /> {uploading ? 'Lädt hoch...' : 'Bild hochladen'}
                      </label>
                      {editingItem.value && (
                        <img src={editingItem.value} alt="Preview" className="image-preview" />
                      )}
                    </div>
                  ) : item.value.length > 100 ? (
                    <Textarea
                      value={editingItem.value}
                      onChange={(e) => setEditingItem({...editingItem, value: e.target.value})}
                      rows={5}
                    />
                  ) : (
                    <Input
                      value={editingItem.value}
                      onChange={(e) => setEditingItem({...editingItem, value: e.target.value})}
                    />
                  )}
                  <div className="editor-actions">
                    <Button onClick={() => onSave(editingItem)} className="btn-primary">
                      <Save size={16} /> Speichern
                    </Button>
                    <Button onClick={() => setEditingItem(null)} className="btn-secondary">
                      <X size={16} /> Abbrechen
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="editor-view-mode">
                  {(item.key === 'team_photo' || item.key === 'additional_photo') && item.value ? (
                    <img src={item.value} alt="Preview" className="editor-image-preview" />
                  ) : (
                    <p className="editor-value">{item.value || '(Leer - Klicken Sie zum Bearbeiten)'}</p>
                  )}
                  <Button onClick={() => setEditingItem(item)} className="btn-icon">
                    <Edit size={16} />
                  </Button>
                </div>
              )}
            </div>
          ))}
        </Card>
      ))}
    </div>
  );
};

// Services Editor Component
const ServicesEditor = ({ services, editingItem, setEditingItem, onSave, onDelete }) => {
  const handleAddNew = () => {
    setEditingItem({
      title: '',
      description: '',
      icon: 'Home',
      order: services.length + 1,
      isNew: true
    });
  };

  return (
    <div className="editor-container">
      <div className="editor-header">
        <h2>Services verwalten</h2>
        <Button onClick={handleAddNew} className="btn-primary">
          <Plus size={16} /> Neuer Service
        </Button>
      </div>

      {editingItem && (
        <Card className="editor-form">
          <h3>{editingItem.isNew ? 'Neuer Service' : 'Service bearbeiten'}</h3>
          <div className="form-group">
            <label>Titel</label>
            <Input
              value={editingItem.title}
              onChange={(e) => setEditingItem({...editingItem, title: e.target.value})}
            />
          </div>
          <div className="form-group">
            <label>Beschreibung</label>
            <Textarea
              value={editingItem.description}
              onChange={(e) => setEditingItem({...editingItem, description: e.target.value})}
              rows={4}
            />
          </div>
          <div className="form-group">
            <label>Icon</label>
            <select
              value={editingItem.icon}
              onChange={(e) => setEditingItem({...editingItem, icon: e.target.value})}
              className="admin-select"
            >
              <option value="Home">Home</option>
              <option value="Car">Car</option>
              <option value="Layers">Layers</option>
              <option value="Flame">Flame</option>
            </select>
          </div>
          <div className="editor-actions">
            <Button onClick={() => onSave(editingItem)} className="btn-primary">
              <Save size={16} /> Speichern
            </Button>
            <Button onClick={() => setEditingItem(null)} className="btn-secondary">
              <X size={16} /> Abbrechen
            </Button>
          </div>
        </Card>
      )}

      <div className="services-grid">
        {services.map(service => (
          <Card key={service.id} className="service-item">
            <h3>{service.title}</h3>
            <p>{service.description}</p>
            <div className="item-actions">
              <Button onClick={() => setEditingItem(service)} className="btn-icon">
                <Edit size={16} />
              </Button>
              <Button onClick={() => onDelete(service.id)} className="btn-icon btn-danger">
                <Trash2 size={16} />
              </Button>
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
};

// Projects Editor Component
const ProjectsEditor = ({ projects, editingItem, setEditingItem, onSave, onDelete, onImageUpload }) => {
  const handleAddNew = () => {
    setEditingItem({
      title: '',
      category: '',
      image_url: '',
      media_type: 'image',
      link_url: '',
      order: projects.length + 1,
      isNew: true
    });
  };

  const handleImageChange = async (e) => {
    const imageUrl = await onImageUpload(e);
    if (imageUrl) {
      // Check if response includes media_type
      const mediaType = e.target.files[0].type.startsWith('video/') ? 'video' : 'image';
      setEditingItem({...editingItem, image_url: imageUrl, media_type: mediaType});
    }
  };

  return (
    <div className="editor-container">
      <div className="editor-header">
        <h2>Projekte verwalten</h2>
        <Button onClick={handleAddNew} className="btn-primary">
          <Plus size={16} /> Neues Projekt
        </Button>
      </div>

      {editingItem && (
        <Card className="editor-form">
          <h3>{editingItem.isNew ? 'Neues Projekt' : 'Projekt bearbeiten'}</h3>
          <div className="form-group">
            <label>Titel</label>
            <Input
              value={editingItem.title}
              onChange={(e) => setEditingItem({...editingItem, title: e.target.value})}
            />
          </div>
          <div className="form-group">
            <label>Kategorie</label>
            <Input
              value={editingItem.category}
              onChange={(e) => setEditingItem({...editingItem, category: e.target.value})}
              placeholder="z.B. Holzbau, Carport, Sauna"
            />
          </div>
          <div className="form-group">
            <label>Link (optional) – beim Klick auf Titel/Kategorie wird diese URL in neuem Tab geöffnet</label>
            <Input
              value={editingItem.link_url || ''}
              onChange={(e) => setEditingItem({...editingItem, link_url: e.target.value})}
              placeholder="https://hansagarten24.at/..."
              data-testid="project-link-url-input"
            />
          </div>
          <div className="form-group">
            <label>Bild oder Video hochladen</label>
            <div className="image-upload">
              <input
                type="file"
                accept="image/*,video/*"
                onChange={handleImageChange}
                id="image-upload"
                className="file-input"
              />
              <label htmlFor="image-upload" className="upload-label">
                <Upload size={20} /> Bild/Video auswählen
              </label>
            </div>
            {editingItem.image_url && (
              <div className="media-preview">
                {editingItem.media_type === 'video' ? (
                  <video src={editingItem.image_url} controls className="image-preview" />
                ) : (
                  <img src={editingItem.image_url} alt="Preview" className="image-preview" />
                )}
              </div>
            )}
          </div>
          <div className="editor-actions">
            <Button onClick={() => onSave(editingItem)} className="btn-primary">
              <Save size={16} /> Speichern
            </Button>
            <Button onClick={() => setEditingItem(null)} className="btn-secondary">
              <X size={16} /> Abbrechen
            </Button>
          </div>
        </Card>
      )}

      <div className="projects-grid">
        {projects.map(project => (
          <Card key={project.id} className="project-item">
            {project.media_type === 'video' ? (
              <video src={project.image_url} className="project-image" controls />
            ) : (
              <img src={project.image_url} alt={project.title} className="project-image" />
            )}
            <div className="project-info">
              <h4>{project.title}</h4>
              <p>{project.category}</p>
              <div className="item-actions">
                <Button onClick={() => setEditingItem(project)} className="btn-icon">
                  <Edit size={16} />
                </Button>
                <Button onClick={() => onDelete(project.id)} className="btn-icon btn-danger">
                  <Trash2 size={16} />
                </Button>
              </div>
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
};

// Messages Viewer Component
const MessagesViewer = ({ messages, onDelete, onMarkAsRead }) => {
  return (
    <div className="editor-container">
      <h2>Kontaktnachrichten</h2>
      <div className="messages-list">
        {messages.length === 0 ? (
          <p className="no-messages">Keine Nachrichten vorhanden</p>
        ) : (
          messages.map(message => (
            <Card key={message.id} className={`message-item ${message.status}`}>
              <div className="message-header">
                <div>
                  <h3>{message.name}</h3>
                  <p className="message-contact">
                    <Mail size={14} /> {message.email}
                    {message.phone && ` • ${message.phone}`}
                  </p>
                </div>
                <span className="message-date">
                  {new Date(message.created_at).toLocaleDateString('de-DE')}
                </span>
              </div>
              <p className="message-text">{message.message}</p>
              <div className="message-actions">
                {message.status === 'new' && (
                  <Button onClick={() => onMarkAsRead(message.id)} className="btn-secondary">
                    <Eye size={16} /> Als gelesen markieren
                  </Button>
                )}
                <Button onClick={() => onDelete(message.id)} className="btn-icon btn-danger">
                  <Trash2 size={16} /> Löschen
                </Button>
              </div>
            </Card>
          ))
        )}
      </div>
    </div>
  );
};

export default AdminDashboard;

// Analytics Viewer Component
const AnalyticsViewer = ({ stats, onRefresh, onReset }) => {
  if (!stats) {
    return <div className="admin-loading">Lädt Statistik...</div>;
  }

  const maxDaily = Math.max(1, ...stats.daily_series.map(d => d.count));
  const formatDay = (iso) => {
    const d = new Date(iso + 'T00:00:00Z');
    return d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' });
  };
  const formatPage = (p) => {
    if (!p) return '/';
    return p === '/' ? 'Startseite (/)' : p;
  };
  const formatReferrer = (r) => {
    try {
      const u = new URL(r);
      return u.hostname.replace(/^www\./, '');
    } catch {
      return r;
    }
  };

  return (
    <div className="editor-container analytics-container" data-testid="analytics-viewer">
      <div className="editor-header">
        <h2>Besucher-Statistik</h2>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <Button onClick={onRefresh} className="btn-secondary" data-testid="analytics-refresh-btn">
            Aktualisieren
          </Button>
          <Button onClick={onReset} className="btn-icon btn-danger" data-testid="analytics-reset-btn">
            <Trash2 size={16} /> Zurücksetzen
          </Button>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="analytics-kpi-grid">
        <Card className="analytics-kpi-card" data-testid="kpi-total-views">
          <div className="kpi-icon"><BarChart3 size={28} /></div>
          <div>
            <p className="kpi-label">Seitenaufrufe gesamt</p>
            <p className="kpi-value">{stats.total_views.toLocaleString('de-DE')}</p>
          </div>
        </Card>
        <Card className="analytics-kpi-card" data-testid="kpi-unique-visitors">
          <div className="kpi-icon"><Users size={28} /></div>
          <div>
            <p className="kpi-label">Einzelne Besucher (30 Tage)</p>
            <p className="kpi-value">{stats.unique_visitors_30d.toLocaleString('de-DE')}</p>
            <p className="kpi-sub">Gesamt: {stats.unique_visitors_total.toLocaleString('de-DE')}</p>
          </div>
        </Card>
        <Card className="analytics-kpi-card" data-testid="kpi-today">
          <div className="kpi-icon"><Calendar size={28} /></div>
          <div>
            <p className="kpi-label">Aufrufe heute</p>
            <p className="kpi-value">{stats.today.toLocaleString('de-DE')}</p>
          </div>
        </Card>
        <Card className="analytics-kpi-card" data-testid="kpi-week">
          <div className="kpi-icon"><TrendingUp size={28} /></div>
          <div>
            <p className="kpi-label">Letzte 7 Tage</p>
            <p className="kpi-value">{stats.last_7_days.toLocaleString('de-DE')}</p>
            <p className="kpi-sub">30 Tage: {stats.last_30_days.toLocaleString('de-DE')}</p>
          </div>
        </Card>
      </div>

      {/* Daily Chart */}
      <Card className="analytics-chart-card">
        <h3 className="analytics-section-title">Aufrufe der letzten 30 Tage</h3>
        {stats.daily_series.every(d => d.count === 0) ? (
          <p className="analytics-empty">Noch keine Aufrufe in den letzten 30 Tagen.</p>
        ) : (
          <div className="analytics-bar-chart" data-testid="analytics-daily-chart">
            {stats.daily_series.map((d) => (
              <div key={d.date} className="bar-column" title={`${formatDay(d.date)}: ${d.count}`}>
                <div className="bar-value">{d.count > 0 ? d.count : ''}</div>
                <div
                  className="bar-fill"
                  style={{ height: `${(d.count / maxDaily) * 100}%` }}
                />
                <div className="bar-label">{formatDay(d.date)}</div>
              </div>
            ))}
          </div>
        )}
      </Card>

      <div className="analytics-two-col">
        {/* Top Pages */}
        <Card className="analytics-list-card">
          <h3 className="analytics-section-title">Top Seiten (30 Tage)</h3>
          {stats.top_pages.length === 0 ? (
            <p className="analytics-empty">Keine Daten</p>
          ) : (
            <ul className="analytics-list" data-testid="analytics-top-pages">
              {stats.top_pages.map((p) => (
                <li key={p.page} className="analytics-list-item">
                  <span className="analytics-list-name">{formatPage(p.page)}</span>
                  <span className="analytics-list-count">{p.count}</span>
                </li>
              ))}
            </ul>
          )}
        </Card>

        {/* Top Referrers */}
        <Card className="analytics-list-card">
          <h3 className="analytics-section-title">Top Verweise (30 Tage)</h3>
          {stats.top_referrers.length === 0 ? (
            <p className="analytics-empty">Keine externen Verweise</p>
          ) : (
            <ul className="analytics-list" data-testid="analytics-top-referrers">
              {stats.top_referrers.map((r) => (
                <li key={r.referrer} className="analytics-list-item">
                  <span className="analytics-list-name" title={r.referrer}>{formatReferrer(r.referrer)}</span>
                  <span className="analytics-list-count">{r.count}</span>
                </li>
              ))}
            </ul>
          )}
        </Card>
      </div>

      <p className="analytics-note">
        Hinweis: Anonymes First-Party-Tracking. IP-Adressen werden mit täglich rotierendem Salt
        gehasht (DSGVO-freundlich), Bots werden ignoriert. „Do Not Track" wird respektiert.
      </p>
    </div>
  );
};
